<template src="./ProjectFormDialog.html"></template>

<script>
import { createProject, updateProject, addProjectMember, removeProjectMember } from '../../services'
import ProjectMemberPicker from './ProjectMemberPicker.vue'

const emptyForm = () => ({
  name: '',
  description: ''
})

export default {
  name: 'ProjectFormDialog',
  components: { ProjectMemberPicker },
  props: {
    value: {
      type: Boolean,
      default: false
    },
    // Si viene con `id`, el diálogo edita; si no, crea. En edición debe incluir `members`
    // ([{ id: <projectMemberId>, user: { id, ... } }]) — ver Projects.vue#openEdit (usa
    // getProject, no getProjects, porque la lista no trae miembros).
    project: {
      type: Object,
      default: null
    }
  },
  model: {
    prop: 'value',
    event: 'update'
  },
  data: () => ({
    form: emptyForm(),
    memberIds: [],
    // Snapshot de los miembros actuales (antes de editar), para calcular el diff a aplicar al
    // guardar — la API solo agrega/quita miembros de a uno, no reemplaza la lista completa.
    existingMembers: [],
    saving: false,
    rules: { required: (val) => !!val || 'Campo requerido' }
  }),
  computed: {
    isEditing () {
      return !!(this.project && this.project.id)
    },
    dialogTitle () {
      return this.isEditing ? 'Editar proyecto' : 'Nuevo proyecto'
    }
  },
  watch: {
    value (isOpen) {
      if (isOpen) { this.resetForm() }
    }
  },
  methods: {
    resetForm () {
      if (this.isEditing) {
        this.form = { name: this.project.name, description: this.project.description || '' }
        this.existingMembers = this.project.members || []
        this.memberIds = this.existingMembers.map((member) => member.user.id)
      } else {
        this.form = emptyForm()
        this.existingMembers = []
        this.memberIds = []
      }
    },
    close () {
      this.$emit('update', false)
    },
    // Aplica el diff entre los miembros que ya tenía el proyecto y los seleccionados ahora en el
    // picker — agrega los nuevos y quita los que se desmarcaron.
    async syncMembers (projectId) {
      const existingUserIds = this.existingMembers.map((member) => member.user.id)
      const toAdd = this.memberIds.filter((userId) => !existingUserIds.includes(userId))
      const toRemove = this.existingMembers.filter((member) => !this.memberIds.includes(member.user.id))

      await Promise.all([
        ...toAdd.map((userId) => addProjectMember(projectId, userId)),
        ...toRemove.map((member) => removeProjectMember(projectId, member.id))
      ])
    },
    async submit () {
      if (!this.form.name) return
      this.saving = true
      try {
        let projectId = this.project?.id
        if (this.isEditing) {
          await updateProject(projectId, { ...this.form })
        } else {
          const created = await createProject({ ...this.form })
          projectId = created.id
        }
        await this.syncMembers(projectId)

        this.$swal({
          text: this.isEditing ? 'Proyecto actualizado.' : 'Proyecto creado.',
          buttons: false,
          icon: 'success',
          timer: 2500
        })
        this.$emit('saved')
        this.close()
      } catch (error) {
        console.error('Error saving project:', error)
        this.$swal({
          text: error.message ?? 'Ocurrió un error, intenta más tarde.',
          buttons: false,
          icon: 'error',
          timer: 3000
        })
      } finally {
        this.saving = false
      }
    }
  }
}
</script>

<style lang="scss" src="./ProjectFormDialog.scss" scoped></style>
